"use strict";
/**
 * Document Enums — new document management system (GH#21).
 *
 * Conventions:
 * - snake_case values per `.claude/rules/enums.md`
 * - `*_LABELS: Record<Enum, string>` Spanish labels alongside each enum
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EXTERNAL_LINK_PROVIDER_LABELS = exports.ExternalLinkProvider = exports.CONTENT_TYPE_LABELS = exports.ContentType = exports.CHANGE_TYPE_LABELS = exports.ChangeType = exports.CRITICALITY_LABELS = exports.Criticality = exports.STATUS_BY_CATEGORY = exports.DOCUMENT_STATUS_LABELS = exports.DocumentStatus = exports.TYPE_TO_CATEGORY = exports.DOCUMENT_TYPE_LABELS = exports.DocumentType = exports.CATEGORY_LABELS = exports.Category = exports.AUDIENCE_ALL = void 0;
// ─── Audience sentinel ──────────────────────────────────────────────────────
/**
 * Sentinel value for `audience: WebAppRole[]` meaning "visible to everyone".
 * Stored as a literal string in the audience array; resolved at query time to
 * skip the role filter.
 */
exports.AUDIENCE_ALL = "all";
// ─── Category ───────────────────────────────────────────────────────────────
/**
 * Fundamental document nature. Drives lifecycle behavior:
 *
 * - `operational` — living doc that evolves; full lifecycle (draft → current →
 *   outdated → withdrawn); has versions; has elaboratedAt/reviewedAt/
 *   authorizedAt per version.
 * - `reference` — singleton authoritative artifact; can be replaced by a newer
 *   instance (which becomes a separate doc); simpler lifecycle.
 * - `record` — moment-in-time capture of an unrepeatable event; no real
 *   lifecycle; just exists.
 */
var Category;
(function (Category) {
    Category["operational"] = "operational";
    Category["reference"] = "reference";
    Category["record"] = "record";
})(Category || (exports.Category = Category = {}));
exports.CATEGORY_LABELS = {
    [Category.operational]: "Operativo",
    [Category.reference]: "Referencia",
    [Category.record]: "Registro",
};
// ─── DocumentType ───────────────────────────────────────────────────────────
/**
 * Specific kind of document within a category. Second axis of classification.
 */
var DocumentType;
(function (DocumentType) {
    // ── operational types (Category.operational) ───────────────────────────
    DocumentType["protocol"] = "protocol";
    DocumentType["guidance"] = "guidance";
    DocumentType["tutorial"] = "tutorial";
    DocumentType["format"] = "format";
    DocumentType["tool"] = "tool";
    DocumentType["policy"] = "policy";
    /**
     * Living registry/tracker. The structure is fixed but the data evolves over
     * time (e.g. monthly income tracker, supplier promotional log). Differs from
     * `format` (a template instance is filled once) and from `reference_data`
     * (typically static reference like catalogs).
     */
    DocumentType["control"] = "control";
    // ── reference types (Category.reference) ───────────────────────────────
    DocumentType["reference_data"] = "reference_data";
    DocumentType["legal_document"] = "legal_document";
    DocumentType["brand_asset"] = "brand_asset";
    /**
     * Permits, certifications, fiscal certificates issued by external authorities.
     * Singletons replaced when re-issued (e.g. CSF SAT, COFEPRIS, MVR cards).
     */
    DocumentType["permit"] = "permit";
    // ── record types (Category.record) ─────────────────────────────────────
    DocumentType["meeting_recording"] = "meeting_recording";
    DocumentType["historical"] = "historical";
    /**
     * Specific instance of a class/training session (e.g. "Otitis externa 1
     * con Anahí Fuentes"). Each is its own doc; new courses ≠ new versions.
     */
    DocumentType["training_class"] = "training_class";
    /**
     * One-shot presentation tied to a specific event (anniversaries, public
     * health campaigns).
     */
    DocumentType["presentation"] = "presentation";
    /**
     * Promotional or marketing content (videos, banners, campaign materials)
     * that captures past-event communication. Different from `presentation`
     * (talk/slide deck) — `marketing` is promotional/external-facing material
     * preserved as historical record.
     */
    DocumentType["marketing"] = "marketing";
})(DocumentType || (exports.DocumentType = DocumentType = {}));
exports.DOCUMENT_TYPE_LABELS = {
    [DocumentType.protocol]: "Protocolo",
    [DocumentType.guidance]: "Lineamiento",
    [DocumentType.tutorial]: "Tutorial",
    [DocumentType.format]: "Formato",
    [DocumentType.tool]: "Herramienta",
    [DocumentType.policy]: "Política",
    [DocumentType.control]: "Control",
    [DocumentType.reference_data]: "Datos de referencia",
    [DocumentType.legal_document]: "Documento legal",
    [DocumentType.brand_asset]: "Activo de marca",
    [DocumentType.permit]: "Permiso / certificado",
    [DocumentType.meeting_recording]: "Grabación de reunión",
    [DocumentType.historical]: "Histórico",
    [DocumentType.training_class]: "Clase de capacitación",
    [DocumentType.presentation]: "Presentación de evento",
    [DocumentType.marketing]: "Marketing",
};
/**
 * Which Category each DocumentType belongs to. Source of truth for category
 * derivation when only `type` is known. Used by validation and queries.
 */
exports.TYPE_TO_CATEGORY = {
    // operational
    [DocumentType.protocol]: Category.operational,
    [DocumentType.guidance]: Category.operational,
    [DocumentType.tutorial]: Category.operational,
    [DocumentType.format]: Category.operational,
    [DocumentType.tool]: Category.operational,
    [DocumentType.policy]: Category.operational,
    [DocumentType.control]: Category.operational,
    // reference
    [DocumentType.reference_data]: Category.reference,
    [DocumentType.legal_document]: Category.reference,
    [DocumentType.brand_asset]: Category.reference,
    [DocumentType.permit]: Category.reference,
    // record
    [DocumentType.meeting_recording]: Category.record,
    [DocumentType.historical]: Category.record,
    [DocumentType.training_class]: Category.record,
    [DocumentType.presentation]: Category.record,
    [DocumentType.marketing]: Category.record,
};
// ─── DocumentStatus ─────────────────────────────────────────────────────────
/**
 * Lifecycle state of the document.
 *
 * Unified vocabulary across categories. Each category uses a subset:
 *   - operational: draft → current → outdated → withdrawn
 *   - reference:           current → outdated → withdrawn
 *   - record:              current → withdrawn (rarely outdated)
 *
 * No automatic direct-publish rules per criticality — author chooses status
 * directly.
 */
var DocumentStatus;
(function (DocumentStatus) {
    DocumentStatus["draft"] = "draft";
    /**
     * Vigente: doc activo y autoritativo (operational), instancia actual
     * (reference) o registro accesible (record).
     */
    DocumentStatus["current"] = "current";
    /**
     * Vigente pero desfasado: el contenido sigue aplicando, pero hay partes
     * desactualizadas que conviene revisar. Visible en listas con un warning.
     */
    DocumentStatus["outdated"] = "outdated";
    /**
     * Retirado: el documento ya no aplica, fue reemplazado o descontinuado.
     * Oculto por defecto en listas. Alineado con ISO/TR 10013 "withdrawn".
     */
    DocumentStatus["withdrawn"] = "withdrawn";
})(DocumentStatus || (exports.DocumentStatus = DocumentStatus = {}));
exports.DOCUMENT_STATUS_LABELS = {
    [DocumentStatus.draft]: "Borrador",
    [DocumentStatus.current]: "Vigente",
    [DocumentStatus.outdated]: "Desfasado",
    [DocumentStatus.withdrawn]: "Retirado",
};
/**
 * Allowed statuses per category. UI uses this to render the right options
 * in dropdowns. Unified vocabulary, but each category uses a subset.
 */
exports.STATUS_BY_CATEGORY = {
    [Category.operational]: [
        DocumentStatus.draft,
        DocumentStatus.current,
        DocumentStatus.outdated,
        DocumentStatus.withdrawn,
    ],
    [Category.reference]: [
        DocumentStatus.current,
        DocumentStatus.outdated,
        DocumentStatus.withdrawn,
    ],
    [Category.record]: [
        DocumentStatus.current,
        DocumentStatus.withdrawn,
    ],
};
// ─── Criticality ────────────────────────────────────────────────────────────
/**
 * How critical the document is. Used for ranking, badge color, and (in SP4)
 * acknowledgment urgency.
 */
var Criticality;
(function (Criticality) {
    Criticality["informational"] = "informational";
    Criticality["standard"] = "standard";
    Criticality["important"] = "important";
    Criticality["critical"] = "critical";
})(Criticality || (exports.Criticality = Criticality = {}));
exports.CRITICALITY_LABELS = {
    [Criticality.informational]: "Informativo",
    [Criticality.standard]: "Estándar",
    [Criticality.important]: "Importante",
    [Criticality.critical]: "Crítico",
};
// ─── ChangeType ─────────────────────────────────────────────────────────────
/**
 * Semver-style classification of a new version's changes.
 *
 * Drives ack invalidation logic in SP4: `major`/`minor` invalidate prior acks;
 * `patch` does not (per master plan §4.3).
 */
var ChangeType;
(function (ChangeType) {
    ChangeType["patch"] = "patch";
    ChangeType["minor"] = "minor";
    ChangeType["major"] = "major";
})(ChangeType || (exports.ChangeType = ChangeType = {}));
exports.CHANGE_TYPE_LABELS = {
    [ChangeType.patch]: "Corrección menor",
    [ChangeType.minor]: "Cambio menor",
    [ChangeType.major]: "Cambio mayor",
};
// ─── ContentType ────────────────────────────────────────────────────────────
/**
 * How the document body is stored.
 *
 * - `markdown`: body is in `DocumentVersion.markdown`
 * - `external_link`: body lives outside the system (Dropbox/YouTube/etc.)
 * - `mixed`: both — markdown wraps an embedded link
 */
var ContentType;
(function (ContentType) {
    ContentType["markdown"] = "markdown";
    ContentType["external_link"] = "external_link";
    ContentType["mixed"] = "mixed";
})(ContentType || (exports.ContentType = ContentType = {}));
exports.CONTENT_TYPE_LABELS = {
    [ContentType.markdown]: "Markdown",
    [ContentType.external_link]: "Enlace externo",
    [ContentType.mixed]: "Mixto",
};
// ─── ExternalLinkProvider ───────────────────────────────────────────────────
/**
 * Known providers for `external_link` content. Drives icon + preview card +
 * disclaimer in the doc viewer.
 */
var ExternalLinkProvider;
(function (ExternalLinkProvider) {
    ExternalLinkProvider["youtube"] = "youtube";
    ExternalLinkProvider["dropbox"] = "dropbox";
    ExternalLinkProvider["google_drive"] = "google_drive";
    ExternalLinkProvider["dailymotion"] = "dailymotion";
    ExternalLinkProvider["other"] = "other";
})(ExternalLinkProvider || (exports.ExternalLinkProvider = ExternalLinkProvider = {}));
exports.EXTERNAL_LINK_PROVIDER_LABELS = {
    [ExternalLinkProvider.youtube]: "YouTube",
    [ExternalLinkProvider.dropbox]: "Dropbox",
    [ExternalLinkProvider.google_drive]: "Google Drive",
    [ExternalLinkProvider.dailymotion]: "Dailymotion",
    [ExternalLinkProvider.other]: "Otro",
};
